import React from "react";
import { Link } from "react-router-dom";
import SminHeader from "@/components/SminHeader";
import SminFooter from "@/components/SminFooter";
import { MARKETING_NAV as NAV } from "@/lib/marketingNav";
import {
  Check, Compass, Mountain, Box, Grid3x3, Calendar, ClipboardList, FileText,
  Cpu, ShieldCheck, Users, Zap, ArrowRight, Server, Database, Cloud, Layers,
} from "lucide-react";

const CORE = [
  { Icon: Database,      title: "Drillhole Data Management", body: "Import Collar / Survey / Assay / Lithology CSVs with automatic column mapping, unit conversion, coordinate reprojection and standards-blanks-duplicates QAQC dashboards. Millions of rows / second bulk ingestion." },
  { Icon: Mountain,      title: "3D Geological Modelling",   body: "Sectional interpretation, lithology domaining, structural modelling and implicit RBF surfaces. Snap-to-drillhole, honour faults, manage nested domains and produce clean volumetric models." },
  { Icon: Box,           title: "Ore Shell & Grade Modelling", body: "Build implicit ore shells with configurable iso-values, calibrated against field statistics. Boolean-combine host geology, faults and grade shells with GPU-accelerated volumetric reporting." },
  { Icon: Grid3x3,       title: "Resource Estimation",       body: "IDW, Nearest Neighbour and Ordinary Kriging with full experimental & modelled variography. Search neighbourhood control, quadrant / octant restrictions and multi-pass estimation." },
  { Icon: Layers,        title: "Block Model & Classification", body: "Regularised block models up to 10M cells. Slope-of-regression confidence classification (Measured / Indicated / Inferred). Sub-blocking, grade-tonnage curves and bulk-density assignment." },
  { Icon: Compass,       title: "Lerchs–Grossmann Pit Optimisation", body: "Bench-level LG max-flow solver up to 500k blocks. Slope zones by rock domain, revenue-factor sensitivity sweeps and nested pushback design in seconds." },
  { Icon: Calendar,      title: "Mine Planning & Design",    body: "Bench-and-ramp geometry with switchbacks, waste dump capacity curves, tailings-storage design and underground stope shells for the pit-to-underground transition." },
  { Icon: ClipboardList, title: "Production Scheduling",     body: "Multi-period NPV-optimised schedules. Discounted cash-flow modelling, sensitivity tornado charts and scenario A/B/C comparison — all inside one workspace." },
  { Icon: FileText,      title: "Technical Reporting",       body: "One-click CRIRSCO · SAMREC · PERC · SME · S-K 1300 · KAZRC · NAEN compliant reports in DOCX / PDF / XLSX / PPTX. Competent-Person sign-off with SHA-256 audit hash." },
  { Icon: Cpu,           title: "AI Assistant",              body: "Ask questions about your project in natural language. Auto-fill workflow parameters, flag QAQC anomalies and draft narratives — always grounded in your project data with a human in the loop." },
  { Icon: Users,         title: "Multi-tenant & RBAC",       body: "Company super-admins, project admins, engineers, geologists and read-only members. SSO / SAML on Enterprise. Full immutable audit trail on every action." },
  { Icon: Zap,           title: "3D WebGL Viewer",           body: "GPU-accelerated instanced rendering of 250,000+ blocks at 60 FPS. Section clipping, classification overlays, pit-shell and pushback visualisation." },
];

const STATS = [
  ["500M+", "assay rows ingested"],
  ["10M",   "block-model cells"],
  ["60 FPS", "3D viewer perf"],
  ["<4d",   "typical project turnaround"],
];

const DEPTH = [
  { Icon: ShieldCheck, title: "Auditable by design", body: "Every plot, table and paragraph in every report is stamped with the exact model version, parameters and dataset SHA-256 hash. Reproducible bit-for-bit years later." },
  { Icon: Server,      title: "Enterprise-grade infra", body: "Region-locked cloud regions in India, EU, Australia and US. Hardened Kubernetes with least-privilege IAM. Fully on-prem and air-gapped deployments supported." },
  { Icon: Cloud,       title: "Zero-install",       body: "Browser-native. No drivers, no admin rights, no per-seat installers. Any modern Chromium / Firefox / Safari. Discrete GPU recommended for large block models." },
  { Icon: Layers,      title: "Portfolio-ready",    body: "Manage 20+ operating assets on a single workspace with unified estimation methodology, block-model geometry and classification rules across the portfolio." },
];

const INTEGRATIONS = [
  "Datamine",    "Vulcan",       "Micromine",  "Leapfrog",
  "GSLIB",       "DXF / DGN",    "Microsoft Office",
  "Okta",        "Microsoft Entra ID", "Google Workspace",
  "PostgreSQL",  "MongoDB",      "Amazon S3",  "Azure Blob",  "Slack",
];

export default function FeaturesPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-neutral-900" data-testid="page-features">
      <SminHeader variant="marketing" forceGuest navLinks={NAV} />

      <main className="flex-1">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-8 text-center">
          <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
            Features
          </div>
          <h1 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight max-w-3xl mx-auto leading-tight">
            Everything a modern mining team needs.
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-neutral-600 text-sm md:text-base leading-relaxed">
            Twelve integrated modules — geology, resource estimation, pit optimisation, scheduling
            and reporting — inside a single browser-based platform. No installers. No hand-offs.
          </p>
        </section>

        {/* Stats bar */}
        <section className="bg-neutral-50 py-10 border-y border-neutral-200">
          <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {STATS.map(([n, l]) => (
              <div key={l}>
                <div className="text-2xl md:text-3xl font-bold tabular-nums" style={{ color: "#4CAF50" }}>{n}</div>
                <div className="mt-1 text-[11px] uppercase tracking-widest text-neutral-500">{l}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Core features grid — 12 cards */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
                Core capabilities
              </div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">Twelve modules. One platform.</h2>
            </div>
            <Link
              to="/modules"
              className="text-sm font-semibold inline-flex items-center gap-1.5"
              style={{ color: "#4CAF50" }}
            >
              Explore modules in depth <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {CORE.map(({ Icon, title, body }) => (
              <article
                key={title}
                data-testid={`feature-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="p-6 rounded-xl border border-neutral-200 hover:border-[#4CAF50] hover:shadow-md transition"
              >
                <span
                  className="inline-flex items-center justify-center h-10 w-10 rounded-md"
                  style={{ background: "#4CAF5015" }}
                >
                  <Icon className="h-5 w-5" style={{ color: "#4CAF50" }} strokeWidth={2} />
                </span>
                <h3 className="mt-4 text-base font-bold">{title}</h3>
                <p className="mt-1.5 text-sm text-neutral-600 leading-relaxed">{body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Depth — "beyond the feature list" */}
        <section className="bg-neutral-50 py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
                Beyond the feature list
              </div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">
                Built for the highest-stakes documents your company owns.
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {DEPTH.map(({ Icon, title, body }) => (
                <div key={title} className="bg-white p-6 rounded-xl border border-neutral-200">
                  <span className="inline-flex items-center justify-center h-10 w-10 rounded-md" style={{ background: "#4CAF5015" }}>
                    <Icon className="h-5 w-5" style={{ color: "#4CAF50" }} strokeWidth={2} />
                  </span>
                  <h3 className="mt-4 text-base font-bold">{title}</h3>
                  <p className="mt-1.5 text-sm text-neutral-600 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Integrations strip */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
              Plays well with your stack
            </div>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">
              Import from legacy, export to anywhere.
            </h2>
            <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
              SMIN slots into the toolchain you already use — no forklift migration required.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {INTEGRATIONS.map((n) => (
              <span
                key={n}
                className="inline-flex items-center h-9 px-4 rounded-full border border-neutral-200 text-xs font-semibold text-neutral-700 hover:border-[#4CAF50] hover:text-[#4CAF50] transition"
              >
                {n}
              </span>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link to="/integrations" className="text-sm font-semibold inline-flex items-center gap-1.5"
                  style={{ color: "#4CAF50" }}>
              Full integrations catalog <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-neutral-50 py-14">
          <div className="max-w-2xl mx-auto text-center px-6">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">See every feature on your own data.</h2>
            <p className="mt-3 text-sm text-neutral-600">
              Book a 30-minute demo — bring a dataset or use ours.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/signup"
                data-testid="features-cta-signup"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md text-white font-semibold text-sm"
                style={{ background: "#4CAF50" }}
              >
                Start free <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="features-cta-demo"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border-2 font-semibold text-sm"
                style={{ borderColor: "#4CAF50", color: "#4CAF50" }}
              >
                Book a demo
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SminFooter />
    </div>
  );
}
